import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Edit2, GripVertical, Save, X, AlertCircle,
  Type, AlignLeft, Link, Mail, Hash, List, CheckSquare, HelpCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text', icon: Type },
  { value: 'textarea', label: 'Long Text', icon: AlignLeft },
  { value: 'url', label: 'URL', icon: Link },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'multiselect', label: 'Multi-Select', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare }
];

const AdminRoundFieldsManager = ({ token }) => {
  const [activeRound, setActiveRound] = useState(1);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [newField, setNewField] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text',
    placeholder: '',
    is_required: true,
    min_length: null,
    max_length: null,
    options: [],
    validation_pattern: '',
    help_text: ''
  });
  const [optionInput, setOptionInput] = useState('');

  useEffect(() => {
    fetchFields();
  }, [activeRound]);

  const fetchFields = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/rounds/${activeRound}/fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFields(response.data.fields || []);
    } catch (err) {
      toast.error('Failed to fetch fields');
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = async () => {
    if (!newField.field_name.trim() || !newField.field_label.trim()) {
      toast.error('Field name and label are required');
      return;
    }

    // Generate field_name from label if not provided
    const fieldName = newField.field_name || newField.field_label.toLowerCase().replace(/\s+/g, '_');

    try {
      const payload = {
        ...newField,
        field_name: fieldName,
        min_length: newField.min_length || null,
        max_length: newField.max_length || null,
        options: newField.options.length > 0 ? newField.options : null
      };

      await axios.post(`${API}/admin/rounds/${activeRound}/fields`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Field added successfully');
      setShowAddModal(false);
      resetNewField();
      fetchFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add field');
    }
  };

  const handleDeleteField = async (fieldId, fieldLabel) => {
    if (!window.confirm(`Are you sure you want to delete "${fieldLabel}"? This will affect all future submissions.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/rounds/${activeRound}/fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field deleted');
      fetchFields();
    } catch (err) {
      toast.error('Failed to delete field');
    }
  };

  const resetNewField = () => {
    setNewField({
      field_name: '',
      field_label: '',
      field_type: 'text',
      placeholder: '',
      is_required: true,
      min_length: null,
      max_length: null,
      options: [],
      validation_pattern: '',
      help_text: ''
    });
    setOptionInput('');
  };

  const addOption = () => {
    if (optionInput.trim() && !newField.options.includes(optionInput.trim())) {
      setNewField(prev => ({
        ...prev,
        options: [...prev.options, optionInput.trim()]
      }));
      setOptionInput('');
    }
  };

  const removeOption = (opt) => {
    setNewField(prev => ({
      ...prev,
      options: prev.options.filter(o => o !== opt)
    }));
  };

  const getFieldIcon = (type) => {
    const fieldType = FIELD_TYPES.find(f => f.value === type);
    return fieldType ? fieldType.icon : Type;
  };

  return (
    <div className="space-y-6" data-testid="round-fields-manager">
      {/* Round Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-4">
        {[1, 2, 3].map(round => (
          <button
            key={round}
            onClick={() => setActiveRound(round)}
            className={`px-6 py-3 rounded-t-lg font-semibold transition-all ${
              activeRound === round
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`round-tab-${round}`}
          >
            Round {round}
          </button>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-800">Round {activeRound} Form Fields</h3>
          <p className="text-sm text-gray-500">
            {fields.length} field{fields.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium"
          data-testid="add-field-btn"
        >
          <Plus size={18} />
          Add Field
        </button>
      </div>

      {/* Fields List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : fields.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
          <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
          <h4 className="font-semibold text-gray-600 mb-2">No fields configured</h4>
          <p className="text-sm text-gray-500 mb-4">
            Add custom fields for teams to fill during Round {activeRound} submission
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Your First Field
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => {
            const Icon = getFieldIcon(field.field_type);
            return (
              <div
                key={field.id}
                className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
                data-testid={`field-${field.id}`}
              >
                <div className="text-gray-400 cursor-grab">
                  <GripVertical size={20} />
                </div>
                <div className={`p-2 rounded-lg ${
                  field.is_required ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Icon size={20} className={field.is_required ? 'text-blue-600' : 'text-gray-500'} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-800">{field.field_label}</h4>
                    {field.is_required && (
                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full">Required</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {FIELD_TYPES.find(f => f.value === field.field_type)?.label || field.field_type}
                    {field.placeholder && ` • "${field.placeholder}"`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDeleteField(field.id, field.field_label)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-field-${field.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Field Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Add Field to Round {activeRound}</h2>
              <button
                onClick={() => { setShowAddModal(false); resetNewField(); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Field Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field Label *
                </label>
                <input
                  type="text"
                  value={newField.field_label}
                  onChange={(e) => setNewField(prev => ({ 
                    ...prev, 
                    field_label: e.target.value,
                    field_name: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                  }))}
                  placeholder="e.g., Project Description"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="field-label-input"
                />
              </div>

              {/* Field Name (auto-generated) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field Name (ID)
                </label>
                <input
                  type="text"
                  value={newField.field_name}
                  onChange={(e) => setNewField(prev => ({ ...prev, field_name: e.target.value }))}
                  placeholder="Auto-generated from label"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  data-testid="field-name-input"
                />
              </div>

              {/* Field Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field Type *
                </label>
                <select
                  value={newField.field_type}
                  onChange={(e) => setNewField(prev => ({ ...prev, field_type: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="field-type-select"
                >
                  {FIELD_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Placeholder */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Placeholder Text
                </label>
                <input
                  type="text"
                  value={newField.placeholder}
                  onChange={(e) => setNewField(prev => ({ ...prev, placeholder: e.target.value }))}
                  placeholder="Hint text shown in the field"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Required Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={newField.is_required}
                  onChange={(e) => setNewField(prev => ({ ...prev, is_required: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 rounded"
                />
                <label htmlFor="is_required" className="text-sm font-medium text-gray-700">
                  Required field
                </label>
              </div>

              {/* Min/Max Length for text fields */}
              {['text', 'textarea'].includes(newField.field_type) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Length
                    </label>
                    <input
                      type="number"
                      value={newField.min_length || ''}
                      onChange={(e) => setNewField(prev => ({ ...prev, min_length: e.target.value ? parseInt(e.target.value) : null }))}
                      placeholder="Optional"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Length
                    </label>
                    <input
                      type="number"
                      value={newField.max_length || ''}
                      onChange={(e) => setNewField(prev => ({ ...prev, max_length: e.target.value ? parseInt(e.target.value) : null }))}
                      placeholder="Optional"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              )}

              {/* Options for select/multiselect */}
              {['select', 'multiselect'].includes(newField.field_type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Options
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={optionInput}
                      onChange={(e) => setOptionInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                      placeholder="Add an option"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={addOption}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {newField.options.map(opt => (
                      <span
                        key={opt}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                      >
                        {opt}
                        <button type="button" onClick={() => removeOption(opt)} className="hover:text-blue-900">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Validation Pattern for URL */}
              {newField.field_type === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL Validation Pattern (Regex)
                  </label>
                  <input
                    type="text"
                    value={newField.validation_pattern}
                    onChange={(e) => setNewField(prev => ({ ...prev, validation_pattern: e.target.value }))}
                    placeholder="e.g., ^https?://github\.com/.+"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for default URL validation</p>
                </div>
              )}

              {/* Help Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Help Text
                </label>
                <input
                  type="text"
                  value={newField.help_text}
                  onChange={(e) => setNewField(prev => ({ ...prev, help_text: e.target.value }))}
                  placeholder="Additional instructions for teams"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={() => { setShowAddModal(false); resetNewField(); }}
                className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAddField}
                className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 flex items-center justify-center gap-2"
                data-testid="save-field-btn"
              >
                <Save size={18} />
                Add Field
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoundFieldsManager;
